import { globalStyle, style } from '@vanilla-extract/css';

import { BUTTON_SHADOW, FOCUS_OUTLINE, tokens } from './tipJarTheme.css';

export const picker = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

export const amounts = style({
  minWidth: 0,
  margin: 0,
  padding: 0,
  border: 0,
});

// Floated so it lays out like any block above the pills, which a fieldset's legend otherwise doesn't.
export const legend = style({
  float: 'left',
  width: '100%',
  margin: '0 0 10px',
  padding: 0,
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '16px',
  color: tokens.muted,
});

const WIDE = 'screen and (min-width: 480px)';

// Every amount on one row, up to four. Five don't fit a 375px phone, so they go three over two, and the wider
// pills below leave "Other" room for a typed amount.
export const options = style({
  clear: 'both',
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '8px',
  selectors: {
    '&[data-count="1"]': { gridTemplateColumns: 'minmax(0, 1fr)' },
    '&[data-count="2"]': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    '&[data-count="3"]': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
    '&[data-count="5"]': { gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' },
  },
  '@media': {
    [WIDE]: {
      selectors: {
        '&[data-count="5"]': { gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' },
      },
    },
  },
});

globalStyle(`${options}[data-count="5"] > :nth-child(-n + 3)`, {
  gridColumn: 'span 2',
  '@media': { [WIDE]: { gridColumn: 'auto' } },
});

globalStyle(`${options}[data-count="5"] > :nth-child(n + 4)`, {
  gridColumn: 'span 3',
  '@media': { [WIDE]: { gridColumn: 'auto' } },
});

export const radio = style({
  position: 'absolute',
  opacity: 0,
  pointerEvents: 'none',
});

export const option = style({
  position: 'relative',
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '48px',
  boxSizing: 'border-box',
  padding: '0 8px',
  border: `1px solid ${tokens.line}`,
  borderRadius: tokens.controlRadius,
  background: tokens.surface,
  color: tokens.ink,
  fontFamily: tokens.buttonFont,
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '20px',
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
  cursor: 'pointer',
  userSelect: 'none',
  boxShadow: '0 1px 2px rgb(0 0 0 / 0.04)',
  transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease, transform 150ms ease',
  selectors: {
    '&:hover': { background: tokens.tint },
    '&:active': { transform: 'scale(0.98)' },
    '&[data-checked="true"]': {
      borderColor: tokens.primary,
      background: tokens.primary,
      color: tokens.onPrimary,
      boxShadow: BUTTON_SHADOW,
    },
    '&:has(input:focus-visible)': FOCUS_OUTLINE,
  },
  '@media': {
    '(prefers-reduced-motion: reduce)': { transition: 'none', transform: 'none' },
  },
});

// "Other" keeps its radio, for keyboard and screen reader users, under the field it turns into.
export const otherOption = style({
  position: 'relative',
  display: 'flex',
});

export const customAmount = style({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  boxSizing: 'border-box',
  padding: '0 12px',
  border: `1px solid ${tokens.primary}`,
  borderRadius: tokens.controlRadius,
  background: tokens.surface,
  color: tokens.ink,
  fontFamily: tokens.buttonFont,
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'text',
  transition: 'box-shadow 150ms ease, border-color 150ms ease',
  selectors: {
    '&:focus-within': { boxShadow: `0 0 0 3px ${tokens.focusRing}` },
    '&:has(input[aria-invalid="true"])': { borderColor: tokens.danger },
  },
});

export const currencySymbol = style({
  flexShrink: 0,
  color: tokens.muted,
});

export const customAmountInput = style({
  flex: 1,
  width: '100%',
  minWidth: 0,
  padding: 0,
  border: 0,
  outline: 'none',
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  // 16px keeps iOS from zooming the page when the field takes focus.
  fontSize: '16px',
  fontVariantNumeric: 'tabular-nums',
});

export const hint = style({
  margin: '10px 0 0',
  fontSize: '13px',
  lineHeight: '18px',
  color: tokens.danger,
});

export const chipIn = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: '52px',
  boxSizing: 'border-box',
  padding: '14px 24px',
  border: 0,
  borderRadius: tokens.controlRadius,
  background: tokens.primary,
  color: tokens.onPrimary,
  fontFamily: tokens.buttonFont,
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  fontVariantNumeric: 'tabular-nums',
  cursor: 'pointer',
  boxShadow: BUTTON_SHADOW,
  transition: 'opacity 150ms ease',
  selectors: {
    '&:hover:not(:disabled)': { opacity: 0.9 },
    '&:disabled': { cursor: 'not-allowed', opacity: 0.5, boxShadow: 'none' },
    '&[aria-busy="true"]': { cursor: 'progress' },
    '&:focus-visible': { ...FOCUS_OUTLINE, outlineOffset: '3px' },
  },
});
